/**
 * Shared trip / itinerary icons for the web app.
 *
 * Maps iOS SF Symbol *meanings* to Lucide glyphs. Do not embed SF Symbols —
 * Apple’s license only allows them on Apple platforms.
 */

import React from 'react';
import type { LucideIcon, LucideProps } from 'lucide-react';
import {
  BedDouble,
  Car,
  CircleCheck,
  Clock,
  Globe,
  Hotel,
  Map,
  MapPin,
  PersonStanding,
  Phone,
  Plane,
  PlaneLanding,
  PlaneTakeoff,
  Ship,
  Utensils,
} from 'lucide-react';
import type { ItineraryItemType } from '../../types/client';

export type TripIconProps = LucideProps;

/** Thinner + absolute stroke keeps Lucide crisp at 13–18px UI sizes. */
const DEFAULT_STROKE = 1.5;

function withDefaults(
  Icon: LucideIcon,
  defaultSize: number
): React.FC<TripIconProps> {
  const Component: React.FC<TripIconProps> = ({
    size = defaultSize,
    strokeWidth = DEFAULT_STROKE,
    absoluteStrokeWidth = true,
    'aria-hidden': ariaHidden = true,
    ...rest
  }) => (
    <Icon
      size={size}
      strokeWidth={strokeWidth}
      absoluteStrokeWidth={absoluteStrokeWidth}
      aria-hidden={ariaHidden}
      {...rest}
    />
  );
  Component.displayName = Icon.displayName ?? Icon.name;
  return Component;
}

/** SF Symbol name → Lucide component (Flutter-ready name parity). */
export const SF_SYMBOL_ICON_MAP: Record<string, LucideIcon> = {
  ferry: Ship,
  ship: Ship,
  airplane: Plane,
  'airplane.arrival': PlaneLanding,
  'airplane.departure': PlaneTakeoff,
  'figure.walk': PersonStanding,
  fork: Utensils,
  'fork.knife': Utensils,
  bed: BedDouble,
  'bed.double': BedDouble,
  mappin: MapPin,
  'mappin.and.ellipse': MapPin,
  map: Map,
  clock: Clock,
  globe: Globe,
  phone: Phone,
  'checkmark.circle.fill': CircleCheck,
  car: Car,
  house: Hotel,
};

export const FerryIcon = withDefaults(Ship, 16);
export const TransportIcon = withDefaults(Plane, 16);
export const ActivityIcon = withDefaults(PersonStanding, 16);
export const MealIcon = withDefaults(Utensils, 16);
export const AccommodationIcon = withDefaults(BedDouble, 16);
export const CarIcon = withDefaults(Car, 16);
export const OtherIcon = withDefaults(MapPin, 16);
export const PinIcon = withDefaults(MapPin, 14);
export const MapIcon = withDefaults(Map, 14);
export const ClockIcon = withDefaults(Clock, 14);
export const GlobeIcon = withDefaults(Globe, 14);
export const PhoneIcon = withDefaults(Phone, 14);
export const CheckIcon = withDefaults(CircleCheck, 14);
export const AirplaneArrivalIcon = withDefaults(PlaneLanding, 18);
export const AirplaneDepartureIcon = withDefaults(PlaneTakeoff, 18);
export const HotelIcon = withDefaults(Hotel, 16);

const TYPE_ICON: Record<ItineraryItemType, React.FC<TripIconProps>> = {
  transport: TransportIcon,
  activity: ActivityIcon,
  meal: MealIcon,
  accommodation: AccommodationIcon,
  note: OtherIcon,
  other: OtherIcon,
};

/** Itinerary item type glyph (meal / activity / transport / accommodation / other). */
export const TypeIcon: React.FC<TripIconProps & { type: ItineraryItemType }> = ({
  type,
  size = 16,
  ...rest
}) => {
  const Icon = TYPE_ICON[type] ?? OtherIcon;
  return <Icon size={size} {...rest} />;
};
